import { useParams, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, Loader2, Smartphone } from 'lucide-react'
import { api } from '@/lib/api'
import { useAuth } from '@/hooks/use-auth'
import { buildAppHref } from '@/lib/auth'
import { formatDuration, formatMetres, formatEpoch, entityRefClass } from '@/lib/format'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'

function DeviceRef({ deviceId }: { deviceId: number | null }) {
  const auth = useAuth()
  if (deviceId == null) return <>{'—'}</>
  return (
    <Link
      to={buildAppHref(`/admin/devices/${deviceId}`, auth)}
      className={entityRefClass}
    >
      Device #{deviceId}
    </Link>
  )
}

export function AdminLinkDetailPage() {
  const { id } = useParams<{ id: string }>()
  const auth = useAuth()

  const { data: link, isLoading, error } = useQuery({
    queryKey: ['admin-link', id],
    queryFn: () => api.adminGetLink(Number(id)),
    enabled: !!id,
  })

  if (isLoading) {
    return (
      <div className="py-12 text-center text-muted-foreground">
        <Loader2 className="inline h-5 w-5 animate-spin mr-2" /> Loading link...
      </div>
    )
  }

  if (error || !link) {
    return (
      <div className="flex flex-col gap-4">
        <div>
          <Button asChild variant="ghost" size="sm">
            <Link to={buildAppHref('/admin/links', auth)} className="no-underline">
              <ArrowLeft className="h-4 w-4 mr-1" /> Back to Links
            </Link>
          </Button>
        </div>
        <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-4 text-destructive">
          {error ? (error as Error).message : 'Link not found'}
        </div>
      </div>
    )
  }

  const fields: [string, React.ReactNode][] = [
    ['ID', link.id],
    ['Text', link.text ?? '—'],
    ['From Device', <DeviceRef key="from" deviceId={link.from_device_id} />],
    ['To Device', <DeviceRef key="to" deviceId={link.to_device_id} />],
    ['Travel Time', formatDuration(link.travel_time)],
    ['Avg Travel Time', formatDuration(link.avg_travel_time)],
    ['Base Travel Time', formatDuration(link.base_travel_time)],
    ['Link Length', formatMetres(link.link_length)],
    ['Speed Limit', link.speed_limit ?? '—'],
    ['Min Travel Time', formatDuration(link.min_travel_time)],
    ['Allowed Variance', formatDuration(link.allowed_variance)],
    ['Time Arrived', formatEpoch(link.time_arrived)],
    ['Project', link.project ?? '—'],
    ['Default Travel Time', formatDuration(link.default_travel_time)],
  ]

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center gap-4">
        <Button asChild variant="ghost" size="sm">
          <Link to={buildAppHref('/admin/links', auth)} className="no-underline">
            <ArrowLeft className="h-4 w-4 mr-1" /> Links
          </Link>
        </Button>
        <h1 className="text-2xl font-bold">Link #{link.id}</h1>
        {link.text && <span className="text-lg text-muted-foreground">({link.text})</span>}
      </div>

      <Card className="bg-card/95 backdrop-blur-sm">
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <tbody>
              {fields.map(([label, value]) => (
                <tr key={label} className="border-b last:border-b-0">
                  <td className="w-48 px-4 py-3 font-medium text-muted-foreground">{label}</td>
                  <td className="px-4 py-3">{value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Related devices summary */}
      {(link.from_device_id != null || link.to_device_id != null) && (
        <Card className="bg-card/95 backdrop-blur-sm">
          <CardContent className="p-5">
            <h2 className="mb-3 text-lg font-semibold flex items-center gap-2">
              <Smartphone className="h-4 w-4" />
              Connected Devices
            </h2>
            <div className="flex gap-4">
              {link.from_device_id != null && (
                <Button asChild variant="outline" size="sm">
                  <Link to={buildAppHref(`/admin/devices/${link.from_device_id}`, auth)} className="no-underline">
                    <Smartphone className="h-4 w-4 mr-1" /> From: Device #{link.from_device_id}
                  </Link>
                </Button>
              )}
              {link.to_device_id != null && (
                <Button asChild variant="outline" size="sm">
                  <Link to={buildAppHref(`/admin/devices/${link.to_device_id}`, auth)} className="no-underline">
                    <Smartphone className="h-4 w-4 mr-1" /> To: Device #{link.to_device_id}
                  </Link>
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
